"use client";

import { FormEvent, useState } from "react";
import { usePathname } from "next/navigation";
import { trackClickEvent } from "@/lib/tracking";

const API_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:4000/graphql";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">(
    "idle"
  );
  const [error, setError] = useState<string | null>(null);
  const path = usePathname();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("loading");
    setError(null);

    const formData = new FormData(e.currentTarget);
    const payload = {
      fullName: String(formData.get("fullName") || ""),
      email: String(formData.get("email") || ""),
      phone: String(formData.get("phone") || ""),
      message: String(formData.get("message") || "")
    };

    try {
      const mutation = `
        mutation AddContact($input: ContactMessageInput!) {
          addContactMessage(input: $input) {
            id
          }
        }
      `;

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: mutation,
          variables: { input: payload }
        })
      });

      const json = await res.json();

      if (json.errors) {
        console.error(json.errors);
        throw new Error("GraphQL error");
      }

      setStatus("success");
      (e.target as HTMLFormElement).reset();

      // Track successful submit
      trackClickEvent(
        "contact_submit",
        path || "/contact",
        payload.fullName || "anonymous"
      );
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError("Something went wrong. Please try again.");
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="gradient-border max-w-xl space-y-4 bg-slate-950/60 p-6"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-xs font-medium text-slate-200">
            Full Name
          </label>
          <input
            name="fullName"
            required
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-200">
            Email
          </label>
          <input
            name="email"
            type="email"
            required
            className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-blue-500"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-200">
          Contact Number
        </label>
        <input
          name="phone"
          className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-blue-500"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-200">
          Short Message
        </label>
        <textarea
          name="message"
          required
          rows={4}
          className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm outline-none focus:border-blue-500"
        />
      </div>

      <button
        type="submit"
        disabled={status === "loading"}
        className="w-full rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-500 disabled:opacity-60"
      >
        {status === "loading" ? "Sending..." : "Submit"}
      </button>

      {status === "success" && (
        <p className="text-xs text-emerald-400">
          Thanks! We&apos;ll get back to you shortly.
        </p>
      )}
      {status === "error" && error && (
        <p className="text-xs text-red-400">{error}</p>
      )}
    </form>
  );
}
